import styled from 'styled-components';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import SiteMapCategoryGroup from './SiteMapCategoryGroup';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import SiteMapShopGroup from './SiteMapShopGroup';
import SiteMapLinksGroup from './SiteMapLinksGroup';
import {
  SITE_MAP_ADDITIONAL_LINKS_SECTION,
  SITE_MAP_CATEGORIES_SECTION,
  SITE_MAP_MAIN_LINKS_SECTION,
  SITE_MAP_STORES_SECTION,
} from '../../../../lib/constants';
import {
  isUserLoggedIn,
} from '../../../../lib/user';
import { extractSiteMapLinks } from '../../../../lib/utils';
import { SiteMapAnchorGroup } from './sitemap.shared';

const PageTitleH1 = styled.h1`
  ${({ theme }) => theme.typographyRebrand.heading['3xl']}
  color: ${p => p.theme.c.text.primary};
  text-transform: uppercase;
  margin-bottom: ${p => p.theme.unit(-1)};
  margin-top: ${p => p.theme.unit(10)};
`;

const SectionGroupDiv = styled.div`
  border-bottom: ${p => (p.isLastChild ? 'none' : `1px solid ${p.theme.c.border.primary}`)};
  margin-bottom: ${p => (p.isLastChild ? p.theme.unit(8) : 'initial')};
`;

const SectionGroupContentDiv = styled.div`
  padding-top: ${p => p.theme.unit(5)};
`;

const SectionGroupLinksContentDiv = styled.div`
  padding-top: ${p => p.theme.unit(5)};
  padding-bottom: ${p => p.theme.unit(5)};
`;

const SectionGroupTitleH2 = styled.h2`
  ${({ theme }) => theme.typographyRebrand.heading['2xl']}
  color: ${({ theme }) => theme.c.text.primary};
  text-transform: uppercase;
  margin-bottom: ${p => p.theme.unit(2)};
  margin-top: 0;
`;

const SiteMapPageContent = (props) => {
  const {
    globalPageConfig = {},
    siteMapPageData = {},
    allCategoriesData = [],
    shopOrder = {},
    locale = 'en',
    footersData = {},
    userData = {},
    navCategoryBarData = {},
    seasonalPageData = [],
    pageStateModifiers = {},
    urlData = {},
  } = props;

  const {
    sectionTitle = [],
  } = siteMapPageData;

  const {
    order = [],
  } = shopOrder;

  const {
    firstRow = [],
    secondRow = [],
    thirdRow = [],
  } = footersData;

  const {
    firstNavSlots = [],
    secondNavSlots = [],
    anonFirstNavSlots = [],
    anonSecondNavSlots = [],
  } = navCategoryBarData;

  const firstNavSlotLinks = isUserLoggedIn(userData)
    ? extractSiteMapLinks(firstNavSlots)
    : extractSiteMapLinks(anonFirstNavSlots);

  const secondNavSlotsLinks = isUserLoggedIn(userData)
    ? extractSiteMapLinks(secondNavSlots)
    : extractSiteMapLinks(anonSecondNavSlots);

  const allMainLinks = (isMobile) => {
    return [...firstNavSlotLinks, ...secondNavSlotsLinks].map((mainLink) => {
      return (
        <SiteMapLinksGroup
          footerLinkData={mainLink}
          locale={locale}
          pageStateModifiers={pageStateModifiers}
          urlData={urlData}
          isMobile={isMobile}
        />
      );
    });
  };

  // Manipulate seasonal page data to have url to open seasoanl pages
  const seasonalPageDataNew = seasonalPageData
    .filter(anySeasonalPage => anySeasonalPage.active && anySeasonalPage.active === 'true')
    .map((seasonalPage) => {
      const seasonalPageWithUrl = Object.assign({}, seasonalPage);
      seasonalPageWithUrl.url = `seasonal/${seasonalPage.slug}`;
      return seasonalPageWithUrl;
  });

  const allAdditionalLinks = [...firstRow, ...secondRow, ...thirdRow, ...seasonalPageDataNew].map((footerLink) => {
    return (
      <SiteMapLinksGroup
        footerLinkData={footerLink}
        locale={locale}
      />
    );
  });

  const allShopLinks = (isMobile) => {
    return order.map((letter) => {
      return (
        <SiteMapShopGroup
          key={`shop-group-${letter}`}
          letter={letter}
          shops={shopOrder[letter]}
          locale={locale}
          isMobile={isMobile} />
      );
    });
  };

  const allCategoryLinks = (isMobile) => {
    return allCategoriesData.map((category) => {
      return (
        <SiteMapCategoryGroup
          key={`category-group-${category.name}`}
          category={category}
          locale={locale}
          globalPageConfig={globalPageConfig}
          isMobile={isMobile} />
      );
    });
  };

  const siteMapSectionGroups = (isMobile) => {
    return sectionTitle.map((section, index, self) => {
      const {
        name = '',
        slug = '',
      } = section;

      const length = self.length;
      const isLastChild = (index === length - 1);

      if (slug === SITE_MAP_MAIN_LINKS_SECTION) {
        return (
          <SectionGroupDiv isLastChild={isLastChild}>
            <SectionGroupLinksContentDiv>
              <SectionGroupTitleH2 isMobile={isMobile}>{name}</SectionGroupTitleH2>
              <SiteMapAnchorGroup>
                {allMainLinks(isMobile)}
              </SiteMapAnchorGroup>
            </SectionGroupLinksContentDiv>
          </SectionGroupDiv>
        );
      } else if (slug === SITE_MAP_ADDITIONAL_LINKS_SECTION) {
        return (
          <SectionGroupDiv isLastChild={isLastChild}>
            <SectionGroupLinksContentDiv>
              <SectionGroupTitleH2 isMobile={isMobile}>{name}</SectionGroupTitleH2>
              <SiteMapAnchorGroup>
                {allAdditionalLinks}
              </SiteMapAnchorGroup>
            </SectionGroupLinksContentDiv>
          </SectionGroupDiv>
        );
      } else if (slug === SITE_MAP_STORES_SECTION) {
        return (
          <SectionGroupDiv isLastChild={isLastChild}>
            <SectionGroupContentDiv>
              <SectionGroupTitleH2 isMobile={isMobile}>{name}</SectionGroupTitleH2>
              {allShopLinks(isMobile)}
            </SectionGroupContentDiv>
          </SectionGroupDiv>
        );
      } else if (slug === SITE_MAP_CATEGORIES_SECTION) {
        return (
          <SectionGroupDiv isLastChild={isLastChild}>
            <SectionGroupContentDiv>
              <SectionGroupTitleH2 isMobile={isMobile}>{name}</SectionGroupTitleH2>
              {allCategoryLinks(isMobile)}
            </SectionGroupContentDiv>
          </SectionGroupDiv>
        );
      }
      return null;
    });
  };

  const {
    displayName = '',
  } = siteMapPageData;

  const desktopPage = (
    <MaxWidthLayout>
      <PageTitleH1>
        {displayName}
      </PageTitleH1>
      <div>
        {siteMapSectionGroups(false)}
      </div>
    </MaxWidthLayout>
  );

  const mobilePage = (
    <MaxWidthLayout>
      <PageTitleH1 isMobile>
        {displayName}
      </PageTitleH1>
      <div>
        {siteMapSectionGroups(true)}
      </div>
    </MaxWidthLayout>
  );

  const renderComp = (
    <MediaQueryHelper
      desktopChildren={desktopPage}
      mobileChildren={mobilePage} />
  );

  return renderComp;
};

export default SiteMapPageContent;
